import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { AbstractControl, FormControl } from '@angular/forms';
import { ReplaySubject, Subscription } from 'rxjs';
import { ValidationErrorService } from 'src/app/core/services/error/validation-error/validation-error.service';

@Component({
  selector: 'app-input',
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
/**
 * Input atom
 */
export class InputComponent implements OnInit, OnDestroy {
  @Input() hint: string = '';
  @Input() label!: string;
  @Input() type: string = '';
  @Input() control!: FormControl;
  @Input() datepicker?: any;
  @Input() autocomplete = true;
  @Input() mask: string = '';
  @Input() maxDate?: string;
  @Input() avoidPasting = false;
  errorMessage = '';
  private subsink = new Subscription();

  private _required: boolean = false;
  isPasswordVisible: boolean = false;
  private _readonly: boolean = false;
  maxLength!: number;
  private _disabled: boolean = false;
  private disabled$: ReplaySubject<boolean> = new ReplaySubject();
  private _isPhoneNumber: boolean = false;

  /**
   * @constructor
   */
  constructor(private validationErrorService: ValidationErrorService) {}

  /**
   * on init
   */
  ngOnInit() {
    this.setErrorMessage(this.control);
    const subscription = this.control.statusChanges.subscribe(() => {
      this.setErrorMessage(this.control);
    });
    this.subsink.add(subscription);
    this.maxLength = this.validationErrorService.getMaximumLength(this.control);
    this.subsink.add(
      this.disabled$.subscribe((disabled) => {
        if (disabled) {
          setTimeout(() => this.control.disable());
        } else {
          setTimeout(() => this.control.enable());
        }
      })
    );
  }

  /**
   * clean up subscriptions
   */
  ngOnDestroy() {
    this.subsink.unsubscribe();
  }

  /**
   * Whether this input is required or not
   */
  @Input()
  get required() {
    return this._required;
  }

  /**
   * Sets this input as required
   * @param {any} value value might be any type and it is coerced into a boolean
   */
  set required(value: any) {
    this._required = coerceBooleanProperty(value);
  }

  /**
   * Whether this input is read-only or not
   */
  @Input()
  get readonly() {
    return this._readonly;
  }

  /**
   * Sets this input as read-only
   * @param {any} value value might be any type and it is coerced into a boolean
   */
  set readonly(value: any) {
    this._readonly = coerceBooleanProperty(value);
  }

  /**
   * Whether this input has a hint attached
   * @return {boolean}
   */
  hasHint(): boolean {
    return this.hint?.length > 0;
  }

  /**
   * Whether this input is a password or not
   * @return {boolean}
   */
  isPassword(): boolean {
    return this.type === 'password';
  }

  /**
   * Which password icon to render
   * @return {string}
   */
  getPasswordIcon(): string {
    return 'visibility' + (this.isPasswordVisible ? '_off' : '');
  }

  /**
   * Toggles visiblity factor
   */
  togglePasswordVisibility(): void {
    this.isPasswordVisible = !this.isPasswordVisible;
  }

  /**
   * Decides input type, which depends on visibility factor
   * @return {string}
   */
  get calculatedType(): string {
    if (this.type === 'password' && this.isPasswordVisible) {
      return 'text';
    }
    return this.type;
  }

  /**
   * Whether this input is a password or not
   * @return {boolean}
   */
  isLoading(): boolean {
    return this.type === 'loading';
  }

  /**
   * Sets error message of this input
   * @param {AbstractControl} abstractControl
   */
  setErrorMessage(abstractControl: AbstractControl) {
    this.validationErrorService
      .translateError(abstractControl)
      .then((errorMessage) => {
        this.errorMessage = errorMessage;
      });
  }

  /**
   * Whether this form field is disabled or not
   */
  @Input()
  get disabled() {
    return this._disabled;
  }

  /**
   * Sets this form field as disabled
   * @param {any} value value might be any type and it is coerced into a boolean
   */
  set disabled(value: any) {
    this._disabled = coerceBooleanProperty(value);
    this.disabled$.next(this._disabled);
  }

  /**
   * Autocomplete accepts values such as off and on, from testing on
   * Google Chrome Chrome Version 108.0.5359.124 (Official Build) (64-bit)
   */
  get autocompleteValue() {
    return this.autocomplete ? 'on' : 'off webauthn';
  }

  /**
   * If this input is a date picker
   */
  get isDatePicker() {
    return coerceBooleanProperty(this.datepicker);
  }

  /**
   * If this input is a text area
   */
  get isTextArea() {
    return this.type === 'textarea';
  }

  /**
   * If this input is a normal input
   */
  get isNormalInput() {
    return !this.isDatePicker && !this.isTextArea && !this.isPhoneNumber;
  }

  /**
   * If this input is a phone number input
   */
  @Input()
  get isPhoneNumber() {
    return this._isPhoneNumber;
  }

  /**
   * Sets this label as a phone number input by using an html attribute
   * @param {any} value
   */
  set isPhoneNumber(value: any) {
    this._isPhoneNumber = coerceBooleanProperty(value);
  }

  /**
   * Avoid pasting event only if avoidPasting input is true
   * @param {ClipboardEvent} event
   */
  onPaste(event: ClipboardEvent): void {
    if (this.avoidPasting) {
      event.preventDefault();
    }
  }
}
